// 設定 → 安全性 → passkey（AUTH-03、§14 #14）：用指紋 / 臉部 / 設備 PIN 無密碼登入，本身就算兩步驟驗證。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../components/ConfirmDialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deletePasskey, listPasskeys, passkeyErrorCode, registerPasskey } from '../../lib/session';
import { passkeySupported } from '../../lib/webauthn';
import { dateStamp, stamp } from '../../lib/timefmt';
import { EmailVerifyPrompt, emailVerifyRequired } from '../../components/EmailVerifyPrompt';

function defaultName(): string {
  const ua = navigator.userAgent;
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Mac/.test(ua) ? 'Mac' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : 'Linux';
  const br = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : '';
  return [br, os].filter(Boolean).join(' on ');
}

export function PasskeysSection() {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['passkeys'], queryFn: () => listPasskeys() });
  const [err, setErr] = useState<string | null>(null);
  /** 403 EMAIL_NOT_VERIFIED →「先驗證你的 email」（kq Q143）；處理了回 true。 */
  const [emailVerify, setEmailVerify] = useState<{ resend: boolean } | null>(null);
  const askEmailVerify = (e: unknown) => {
    const ev = emailVerifyRequired(e);
    if (ev) setEmailVerify(ev);
    return ev !== null;
  };
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['passkeys'] });
    void qc.invalidateQueries({ queryKey: ['mfa-status'] });
  };
  const showErr = (e: unknown) => {
    if (askEmailVerify(e)) return;
    const c = passkeyErrorCode(e);
    setErr(t(`passkey.err_${c}`, { code: c, defaultValue: t('passkey.err_default', { code: c }) }));
  };
  const add = useMutation({ mutationFn: () => registerPasskey(defaultName()), onSuccess: () => (setErr(null), refresh()), onError: showErr });
  const del = useMutation({ mutationFn: (id: string) => deletePasskey(id), onSuccess: () => (setErr(null), refresh()), onError: showErr });
  const list = q.data ?? [];
  return (
    <section className="card">
      <div className="toolbar">
        <h2 className="section-title">{t('passkey.title')}</h2>
        <div className="spacer" />
        {passkeySupported() && (
          <button className="btn btn-primary btn-sm" disabled={add.isPending} onClick={() => add.mutate()}>
            {t('passkey.add')}
          </button>
        )}
      </div>
      <p className="muted small">{t('passkey.hint')}</p>
      {!passkeySupported() && <p className="notice">{t('passkey.unsupported')}</p>}
      {err && <p className="error">{err}</p>}
      {q.isLoading && <p className="muted">{t('common.loading')}</p>}
      {list.length === 0 && !q.isLoading && <p className="muted small">{t('passkey.none')}</p>}
      <ul className="plain-list">
        {list.map((p) => (
          <li key={p.id} className="row">
            <strong>{p.name}</strong>
            {p.backup_eligible && <span className="chip">{t('passkey.synced')}</span>}
            <span className="muted small">
              {t('passkey.created', { date: dateStamp(p.created_at) })}
              {p.last_used_at && ' · ' + t('passkey.last_used', { date: stamp(p.last_used_at) })}
            </span>
            <div className="spacer" />
            <button
              className="btn btn-sm"
              disabled={del.isPending}
              onClick={() => {
                void ask({ danger: true, title: t('passkey.delete_confirm', { name: p.name }), action: t('common.action_delete') }).then((ok) => ok && del.mutate(p.id));
              }}
            >
              {t('passkey.delete')}
            </button>
          </li>
        ))}
      </ul>
      {dialog}
      {emailVerify && <EmailVerifyPrompt resend={emailVerify.resend} onClose={() => setEmailVerify(null)} />}
    </section>
  );
}
